import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import React from 'react';

import { ActionButton } from '../../services/ai/omniscientAgentService';

interface EnhancedMessageProps {
  content: string;
  isAI: boolean;
  timestamp?: string;
  actions?: ActionButton[];
  agentUsed?: string;
  confidence?: number;
  onActionClick?: (action: ActionButton) => void;
}

const EnhancedMessage: React.FC<EnhancedMessageProps> = ({
  content,
  isAI,
  timestamp,
  actions,
  agentUsed,
  confidence,
  onActionClick,
}) => {
  const theme = useTheme();

  const handleActionClick = (action: ActionButton) => {
    if (onActionClick) {
      onActionClick(action);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isAI ? 'flex-start' : 'flex-end',
        gap: 1,
        mb: 2,
      }}
    >
      {isAI && (
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <SmartToyIcon />
        </Avatar>
      )}
      
      <Paper
        elevation={0}
        sx={{
          p: 2,
          maxWidth: '70%',
          bgcolor: isAI
            ? alpha(theme.palette.primary.main, 0.1)
            : 'primary.main',
          color: isAI ? 'text.primary' : 'primary.contrastText',
          borderRadius: 2,
          border: isAI ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
        }}
      >
        {/* Message Content */}
        <Typography variant="body1" sx={{ mb: 1 }}>
          {content}
        </Typography>

        {/* AI-specific enhancements */}
        {isAI && (
          <Box sx={{ mt: 2 }}>
            {/* Agent and confidence info */}
            {(agentUsed || confidence) && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {agentUsed && (
                  <Chip
                    label={`Agent: ${agentUsed}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.7rem',
                      height: '20px',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                )}
                {confidence && (
                  <Chip
                    label={`Confidence: ${Math.round(confidence * 100)}%`}
                    size="small"
                    variant="outlined"
                    color={confidence > 0.8 ? 'success' : confidence > 0.6 ? 'warning' : 'error'}
                    sx={{
                      fontSize: '0.7rem',
                      height: '20px',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                )}
              </Box>
            )}

            {/* Action Buttons */}
            {actions && actions.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    size="small"
                    variant={action.primary ? 'contained' : 'outlined'}
                    onClick={() => handleActionClick(action)}
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      minWidth: 'auto',
                      textTransform: 'none',
                      bgcolor: action.primary ? 'primary.main' : 'transparent',
                      color: action.primary ? 'white' : 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        bgcolor: action.primary ? 'primary.dark' : alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Timestamp */}
        {timestamp && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 1,
              fontSize: '0.7rem',
              opacity: 0.7,
            }}
          >
            {timestamp}
          </Typography>
        )}
      </Paper>

      {!isAI && <Avatar sx={{ bgcolor: 'secondary.main' }}>U</Avatar>}
    </Box>
  );
};

export default EnhancedMessage; 